import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Empty, Modal, Chip, EmbeddedActionBar } from "../components/ui-bits";
import { Plus, Trash2, Edit3, Search, Archive, ArchiveRestore, X, Boxes } from "lucide-react";
import { OrderMaterialsModal } from "../components/OrderMaterialsModal";
import { useBrandPermissions } from "../lib/brand";
import { useT } from "../lib/i18n";
import { useAutoOpenFromQuery } from "../lib/useAutoOpenFromQuery";

// -------- enums (mirror backend) --------
const ORDER_TYPES = ["Commessa MMS", "Ordine B2B", "Ordine e-commerce"];
const SOURCES = ["Manuale", "Shopify", "Wix", "WooCommerce"];
const STATUSES = ["Bozza", "Preventivo", "Confermato", "In produzione", "Pronto", "Spedito", "Completato", "Annullato"];
const PAY_STATUSES = ["Da pagare", "Parziale", "Pagato", "Rimborsato"];

// Status → chip colour (tokens keep it correct across light/dark & brand palettes)
function statusChipStyle(v) {
  const map = {
    "Bozza":          { bg: "rgba(115,115,115,.16)", fg: "var(--text-primary)" },
    "Preventivo":     { bg: "rgba(115,115,115,.16)", fg: "var(--text-primary)" },
    "Confermato":     { bg: "rgba(37,99,235,.16)",   fg: "#1D4ED8" },
    "In produzione":  { bg: "rgba(217,119,6,.18)",   fg: "#A45A05" },
    "Pronto":         { bg: "rgba(15,133,84,.16)",   fg: "#0F8554" },
    "Spedito":        { bg: "rgba(6,148,162,.16)",   fg: "#0694A2" },
    "Completato":     { bg: "rgba(15,118,110,.18)",  fg: "#0F766E" },
    "Annullato":      { bg: "rgba(220,38,38,.14)",   fg: "#B91C1C" },
  };
  return map[v] || map["Bozza"];
}
function payChipStyle(v) {
  const map = {
    "Da pagare":  { bg: "rgba(220,38,38,.12)", fg: "#B91C1C" },
    "Parziale":   { bg: "rgba(217,119,6,.18)", fg: "#A45A05" },
    "Pagato":     { bg: "rgba(15,133,84,.16)", fg: "#0F8554" },
    "Rimborsato": { bg: "rgba(115,115,115,.16)", fg: "var(--text-primary)" },
  };
  return map[v] || map["Da pagare"];
}
function sourceChipStyle(v) {
  const map = {
    "Manuale":    { bg: "rgba(115,115,115,.14)", fg: "var(--text-primary)" },
    "Shopify":    { bg: "rgba(15,133,84,.16)",   fg: "#0F8554" },
    "Wix":        { bg: "rgba(93,60,180,.16)",   fg: "#5D3CB4" },
    "WooCommerce":{ bg: "rgba(122,49,167,.16)",  fg: "#7A31A7" },
  };
  return map[v] || map["Manuale"];
}
function DotChip({ value, style, testId }) {
  const s = style(value);
  return (
    <span className="chip" data-testid={testId}
      style={{ background: s.bg, color: s.fg, borderColor: s.bg }} title={value}>
      <span className="w-1.5 h-1.5 rounded-full inline-block mr-1.5" style={{ background: s.fg }}/>
      {value || "—"}
    </span>
  );
}

const emptyLine = () => ({
  garment_id: "", product_name: "", sku: "", size: "", color: "",
  quantity: 1, unit_price: 0, discount_pct: 0, tax_rate: null,
});
const emptyForm = () => ({
  order_type: "Ordine B2B",
  client_id: "",
  collection_id: "",
  currency: "EUR",
  tax_rate: 0.22,
  lines: [],
  status: "Bozza",
  payment_status: "Da pagare",
  order_date: new Date().toISOString().slice(0, 10),
  delivery_date: "",
  notes: "",
});

// -------- pure calc for totals shown inside the modal (backend recomputes on save) --------
function calcLine(ln, orderRate) {
  const qty = parseFloat(ln.quantity || 0) || 0;
  const price = parseFloat(ln.unit_price || 0) || 0;
  const disc = parseFloat(ln.discount_pct || 0) || 0;
  const gross = qty * price;
  const discount = gross * (disc / 100);
  const taxable = gross - discount;
  const rate = ln.tax_rate == null ? parseFloat(orderRate || 0) : parseFloat(ln.tax_rate || 0);
  const tax = taxable * (rate || 0);
  return { gross, discount, taxable, tax, total: taxable + tax };
}
function calcTotals(lines, orderRate) {
  let subtotal = 0, discount = 0, tax = 0;
  for (const ln of lines) { const c = calcLine(ln, orderRate); subtotal += c.taxable; discount += c.discount; tax += c.tax; }
  return { subtotal: r2(subtotal), discount: r2(discount), tax: r2(tax), total: r2(subtotal + tax) };
}
const r2 = (n) => Math.round(n * 100) / 100;
const fmtMoney = (n, cur = "EUR") =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: cur || "EUR" }).format(n || 0);

// ==================================================================
export default function OrdersPage({ embedded = false } = {}) {
  const { canEdit, canAdmin } = useBrandPermissions();
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [garments, setGarments] = useState([]);
  const [collections, setCollections] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ status: "", order_type: "", source: "", payment_status: "", include_archived: false });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [paymentBanner, setPaymentBanner] = useState("");
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [matOrderId, setMatOrderId] = useState(null);
  useEffect(() => {
    const mid = searchParams.get("materials");
    if (mid) setMatOrderId(mid);
  }, [searchParams]);

  const load = useCallback(() => {
    const p = { search: search || undefined };
    if (filters.status)         p.status = filters.status;
    if (filters.order_type)     p.order_type = filters.order_type;
    if (filters.source)         p.source = filters.source;
    if (filters.payment_status) p.payment_status = filters.payment_status;
    if (filters.include_archived) p.include_archived = true;
    return api.get("/orders", { params: p }).then(r => setList(r.data));
  }, [search, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data));
    api.get("/garments").then(r => setGarments(r.data));
    api.get("/collections").then(r => setCollections(r.data));
  }, []);

  // Stripe return banner (legacy). Kept working.
  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (sid) {
      api.get(`/payments/status/${sid}`).then(r => {
        setPaymentBanner(r.data.status === "paid" ? "Pagamento riuscito. Grazie!" : `Stato pagamento: ${r.data.status}`);
      }).catch(() => setPaymentBanner("Verifica pagamento non riuscita."));
      const p = new URLSearchParams(searchParams); p.delete("session_id"); setSearchParams(p, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const clientName = (id) => {
    const c = clients.find(x => x.id === id);
    return c ? (c.name || c.brand || "—") : "—";
  };

  const open = (row) => {
    setError(""); setFieldErrors({});
    if (row) {
      setForm({
        ...emptyForm(),
        ...row,
        lines: (row.lines || []).map(l => ({ ...emptyLine(), ...l })),
        order_date: row.order_date || row.created_at?.slice(0, 10) || "",
        delivery_date: row.delivery_date || row.due_date || "",
      });
      setEditId(row.id);
    } else {
      setForm(emptyForm()); setEditId(null);
    }
    setModal(true);
  };
  useAutoOpenFromQuery(list, open);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setLine = (i, patch) => setForm(f => {
    const lines = f.lines.slice();
    lines[i] = { ...lines[i], ...patch };
    return { ...f, lines };
  });
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));

  const totals = useMemo(() => calcTotals(form.lines, form.tax_rate), [form.lines, form.tax_rate]);

  const validate = () => {
    const errs = {};
    if (!form.client_id) errs.client_id = t("order.err.client_required");
    if (!form.lines?.length) errs.lines = t("order.err.line_required");
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const save = async (e) => {
    e.preventDefault(); setError("");
    if (!validate()) return;
    setSaving(true);
    // Payload: strip immutable/technical + normalise numeric types
    const payload = {
      order_type: form.order_type,
      client_id: form.client_id,
      collection_id: form.collection_id || null,
      currency: form.currency,
      tax_rate: parseFloat(form.tax_rate) || 0,
      status: form.status,
      payment_status: form.payment_status,
      order_date: form.order_date || null,
      delivery_date: form.delivery_date || null,
      notes: form.notes || null,
      lines: form.lines.map(l => ({
        garment_id: l.garment_id || null,
        product_name: l.product_name || null,
        sku: l.sku || null,
        size: l.size || null,
        color: l.color || null,
        quantity: parseInt(l.quantity) || 0,
        unit_price: parseFloat(l.unit_price) || 0,
        discount_pct: parseFloat(l.discount_pct) || 0,
        tax_rate: l.tax_rate === "" || l.tax_rate == null ? null : parseFloat(l.tax_rate),
      })),
    };
    try {
      if (editId) await api.put(`/orders/${editId}`, payload);
      else { const r = await api.post("/orders", payload); setMatOrderId(r.data.id); }
      setModal(false); load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const quickStatus = async (row, status, payment_status) => {
    const body = {};
    if (status) body.status = status;
    if (payment_status) body.payment_status = payment_status;
    await api.post(`/orders/${row.id}/status`, body); load();
  };
  const archive = async (row) => {
    if (!window.confirm(t("order.confirm.archive"))) return;
    await api.post(`/orders/${row.id}/archive`); load();
  };
  const unarchive = async (row) => {
    await api.post(`/orders/${row.id}/unarchive`); load();
  };

  // -------- New client quick-add (reuses backend endpoint) --------
  const [nc, setNc] = useState({ client_type: "shop", name: "", email: "" });
  const [ncErr, setNcErr] = useState("");
  const saveNewClient = async (e) => {
    e.preventDefault(); setNcErr("");
    if (!nc.name?.trim() || !nc.email?.trim()) { setNcErr(t("client.err.required")); return; }
    try {
      const { data } = await api.post("/clients", nc);
      const list = (await api.get("/clients")).data;
      setClients(list);
      setForm(f => ({ ...f, client_id: data.id }));
      setClientModalOpen(false);
      setNc({ client_type: "shop", name: "", email: "" });
    } catch (err) {
      setNcErr(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="orders-page">
      <OrderMaterialsModal orderId={matOrderId} onClose={()=>{
        setMatOrderId(null);
        if (searchParams.get("materials")) { const p = new URLSearchParams(searchParams); p.delete("materials"); setSearchParams(p, { replace: true }); }
      }}/>
      {!embedded && (
      <PageHeader eyebrow="Sales & Invoicing" title={t("order.title")}
        action={canEdit && <button data-testid="new-order-btn" onClick={()=>open(null)} className="btn-gold inline-flex items-center gap-2"><Plus size={14}/>{t("order.new")}</button>}/>
      )}

      {embedded && canEdit && (
        <EmbeddedActionBar action={
          <button data-testid="new-order-btn" onClick={()=>open(null)} className="btn-gold inline-flex items-center gap-2">
            <Plus size={14}/> {t("order.new")}
          </button>
        }/>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted,#737373)]"/>
          <input data-testid="orders-search" placeholder={t("common.search")} value={search} onChange={(e)=>setSearch(e.target.value)} className="input-dark pl-9"/>
        </div>
        <select data-testid="orders-filter-status" className="input-dark max-w-xs" value={filters.status} onChange={(e)=>setFilters(f=>({...f, status: e.target.value}))}>
          <option value="">{t("order.filter.all_statuses")}</option>
          {STATUSES.map(s=><option key={s}>{s}</option>)}
        </select>
        <select data-testid="orders-filter-type" className="input-dark max-w-xs" value={filters.order_type} onChange={(e)=>setFilters(f=>({...f, order_type: e.target.value}))}>
          <option value="">{t("order.filter.all_types")}</option>
          {ORDER_TYPES.map(s=><option key={s}>{s}</option>)}
        </select>
        <select data-testid="orders-filter-source" className="input-dark max-w-xs" value={filters.source} onChange={(e)=>setFilters(f=>({...f, source: e.target.value}))}>
          <option value="">{t("order.filter.all_sources")}</option>
          {SOURCES.map(s=><option key={s}>{s}</option>)}
        </select>
        <select data-testid="orders-filter-pay" className="input-dark max-w-xs" value={filters.payment_status} onChange={(e)=>setFilters(f=>({...f, payment_status: e.target.value}))}>
          <option value="">{t("order.filter.all_pay")}</option>
          {PAY_STATUSES.map(s=><option key={s}>{s}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-xs">
          <input type="checkbox" data-testid="orders-filter-include-archived" checked={filters.include_archived} onChange={(e)=>setFilters(f=>({...f, include_archived: e.target.checked}))}/>
          {t("order.filter.include_archived")}
        </label>
      </div>

      {paymentBanner && (
        <div className="card-dark p-3 mb-4 text-sm border-l-2 border-l-[color:var(--text-primary)]" data-testid="payment-banner">{paymentBanner}</div>
      )}

      {list.length === 0 ? (
        <Empty title={t("order.empty")}
          cta={canEdit ? { label: t("order.cta_first"), onClick: ()=>open(null) } : null}
          ctaTestId="orders-empty-cta"/>
      ) : (
        <div className="card-dark overflow-x-auto">
          <table className="tbl" data-testid="orders-table">
            <thead><tr>
              <th>{t("order.tbl.number")}</th>
              <th>{t("order.tbl.client")}</th>
              <th>{t("order.tbl.type")}</th>
              <th>{t("order.tbl.source")}</th>
              <th>{t("order.tbl.date")}</th>
              <th>{t("order.tbl.delivery")}</th>
              <th>{t("order.tbl.total")}</th>
              <th>{t("order.tbl.payment")}</th>
              <th>{t("order.tbl.status")}</th>
              <th></th>
            </tr></thead>
            <tbody>
              {list.map(o => (
                <tr key={o.id} data-testid={`order-row-${o.id}`}>
                  <td className="font-medium">{o.order_number}</td>
                  <td>{clientName(o.client_id)}</td>
                  <td className="text-xs">{o.order_type || "—"}</td>
                  <td><DotChip value={o.source || "Manuale"} style={sourceChipStyle} testId={`order-source-${o.id}`}/></td>
                  <td className="text-xs">{(o.order_date || o.created_at || "").slice(0, 10) || "—"}</td>
                  <td className="text-xs">{(o.delivery_date || o.due_date || "—")}</td>
                  <td className="text-right">{fmtMoney(o.total, o.currency)}</td>
                  <td><DotChip value={o.payment_status || "Da pagare"} style={payChipStyle}/></td>
                  <td>
                    {canEdit ? (
                      <select
                        data-testid={`order-status-${o.id}`}
                        className="input-dark py-1 text-xs"
                        value={o.status || "Bozza"}
                        onChange={(e)=>quickStatus(o, e.target.value)}
                        style={{ minWidth: 140 }}
                      >
                        {STATUSES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    ) : <DotChip value={o.status || "Bozza"} style={statusChipStyle}/>}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={()=>setMatOrderId(o.id)} title="Materiali necessari" className="text-[color:var(--text-secondary,#525252)] hover:text-[color:var(--text-primary)] mr-3" data-testid={`order-materials-${o.id}`}><Boxes size={14}/></button>
                    {canEdit && <>
                      <button onClick={()=>open(o)} title={t("common.edit")} className="text-[color:var(--text-secondary,#525252)] hover:text-[color:var(--text-primary)] mr-3" data-testid={`order-edit-${o.id}`}><Edit3 size={14}/></button>
                      {o.archived ? (
                        <button onClick={()=>unarchive(o)} title={t("order.action.unarchive")} className="text-[color:var(--text-secondary,#525252)] hover:text-[color:var(--text-primary)]" data-testid={`order-unarchive-${o.id}`}><ArchiveRestore size={14}/></button>
                      ) : (
                        <button onClick={()=>archive(o)} title={t("order.action.archive")} className="text-[color:var(--text-secondary,#525252)] hover:text-[color:var(--text-primary)]" data-testid={`order-archive-${o.id}`}><Archive size={14}/></button>
                      )}
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ============ Order Modal ============ */}
      <Modal open={modal} onClose={()=>setModal(false)} title={editId ? t("order.modal.edit") : t("order.modal.new")} wide>
        <form onSubmit={save} className="space-y-6">
          {/* Section: summary */}
          <section>
            <div className="label-caps mb-3">{t("order.section.summary")}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label={t("order.field.order_number")}>
                <input data-testid="order-form-order_number" className="input-dark opacity-70" disabled placeholder={editId ? form.order_number : "Auto"} value={form.order_number || ""}/>
              </Field>
              <Field label={t("order.field.order_type")} required>
                <select data-testid="order-form-order_type" className="input-dark" value={form.order_type} onChange={(e)=>setField("order_type", e.target.value)}>
                  {ORDER_TYPES.map(v=><option key={v}>{v}</option>)}
                </select>
              </Field>
              <Field label={t("order.field.source")}>
                <div className="input-dark opacity-70 pointer-events-none flex items-center">
                  <DotChip value={editId ? (form.source || "Manuale") : "Manuale"} style={sourceChipStyle}/>
                </div>
              </Field>
              <Field label={t("order.field.client")} required error={fieldErrors.client_id}>
                <div className="flex gap-2">
                  <select data-testid="order-form-client_id" required className="input-dark" value={form.client_id||""} onChange={(e)=>setField("client_id", e.target.value)}>
                    <option value="">—</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name || c.brand}</option>)}
                  </select>
                  {canEdit && (
                    <button type="button" onClick={()=>setClientModalOpen(true)} className="btn-ghost whitespace-nowrap" data-testid="order-form-new-client">
                      + {t("order.new_client_hint")}
                    </button>
                  )}
                </div>
              </Field>
              <Field label={t("order.field.collection")}>
                <select data-testid="order-form-collection_id" className="input-dark" value={form.collection_id||""} onChange={(e)=>setField("collection_id", e.target.value)}>
                  <option value="">—</option>
                  {collections.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label={t("order.field.currency")}>
                <select data-testid="order-form-currency" className="input-dark" value={form.currency||"EUR"} onChange={(e)=>setField("currency", e.target.value)}>
                  {["EUR","USD","GBP","CHF","JPY"].map(c=><option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label={t("order.field.tax_rate")}>
                <input data-testid="order-form-tax_rate" type="number" step="0.01" min="0" max="1" className="input-dark"
                  value={form.tax_rate ?? 0.22}
                  onChange={(e)=>setField("tax_rate", e.target.value)} />
              </Field>
              <Field label={t("order.field.order_date")}>
                <input data-testid="order-form-order_date" type="date" className="input-dark" value={form.order_date||""} onChange={(e)=>setField("order_date", e.target.value)}/>
              </Field>
              <Field label={t("order.field.delivery_date")}>
                <input data-testid="order-form-delivery_date" type="date" className="input-dark" value={form.delivery_date||""} onChange={(e)=>setField("delivery_date", e.target.value)}/>
              </Field>
              <Field label={t("order.field.status")}>
                <select data-testid="order-form-status" className="input-dark" value={form.status} onChange={(e)=>setField("status", e.target.value)}>
                  {STATUSES.map(s=><option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={t("order.field.payment_status")}>
                <select data-testid="order-form-payment_status" className="input-dark" value={form.payment_status} onChange={(e)=>setField("payment_status", e.target.value)}>
                  {PAY_STATUSES.map(s=><option key={s}>{s}</option>)}
                </select>
              </Field>
            </div>
          </section>

          {/* Section: lines */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="label-caps">{t("order.section.lines")}</div>
              <button type="button" onClick={addLine} className="btn-ghost inline-flex items-center gap-2 text-xs" data-testid="order-add-line">
                <Plus size={12}/> {t("order.add_line")}
              </button>
            </div>
            {fieldErrors.lines && <div className="text-red-600 text-xs mb-2">{fieldErrors.lines}</div>}
            {form.lines.length === 0 ? (
              <div className="border border-dashed border-[color:var(--border,#E5E5E5)] p-6 text-center text-xs text-[color:var(--text-muted,#737373)]">
                {t("order.err.line_required")}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="order-lines-table">
                  <thead className="text-[color:var(--text-muted,#737373)] uppercase tracking-widest text-[10px]">
                    <tr>
                      <th className="text-left py-2 pr-2 min-w-[180px]">{t("order.line.product")}</th>
                      <th className="text-left py-2 pr-2 min-w-[130px]">{t("order.line.sku")}</th>
                      <th className="text-left py-2 pr-2">{t("order.line.color")}</th>
                      <th className="text-left py-2 pr-2 w-16">{t("order.line.size")}</th>
                      <th className="text-right py-2 pr-2 w-14">{t("order.line.quantity")}</th>
                      <th className="text-right py-2 pr-2 w-24">{t("order.line.unit_price")}</th>
                      <th className="text-right py-2 pr-2 w-20">{t("order.line.discount")}</th>
                      <th className="text-right py-2 pr-2 w-20">{t("order.line.tax")}</th>
                      <th className="text-right py-2 pr-2 w-24">{t("order.line.line_total")}</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((ln, i) => {
                      const c = calcLine(ln, form.tax_rate);
                      return (
                        <tr key={i} data-testid={`order-line-${i}`}>
                          <td className="pr-2 py-1">
                            <div className="flex gap-1">
                              <select className="input-dark text-xs" value={ln.garment_id||""} onChange={(e)=>{
                                const g = garments.find(g=>g.id===e.target.value);
                                setLine(i, { garment_id: e.target.value, product_name: g?.name || ln.product_name });
                              }} data-testid={`order-line-${i}-garment`}>
                                <option value="">—</option>
                                {garments.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                              </select>
                            </div>
                            <input className="input-dark text-xs mt-1" placeholder={t("order.line.product")} value={ln.product_name||""} onChange={(e)=>setLine(i, { product_name: e.target.value })} data-testid={`order-line-${i}-product_name`}/>
                          </td>
                          <td className="pr-2"><input className="input-dark text-xs" value={ln.sku||""} onChange={(e)=>setLine(i,{sku:e.target.value})} data-testid={`order-line-${i}-sku`}/></td>
                          <td className="pr-2"><input className="input-dark text-xs" value={ln.color||""} onChange={(e)=>setLine(i,{color:e.target.value})} data-testid={`order-line-${i}-color`}/></td>
                          <td className="pr-2"><input className="input-dark text-xs" value={ln.size||""} onChange={(e)=>setLine(i,{size:e.target.value})} data-testid={`order-line-${i}-size`}/></td>
                          <td className="pr-2"><input type="number" min="0" className="input-dark text-xs text-right" value={ln.quantity} onChange={(e)=>setLine(i,{quantity:e.target.value})} data-testid={`order-line-${i}-qty`}/></td>
                          <td className="pr-2"><input type="number" step="0.01" min="0" className="input-dark text-xs text-right" value={ln.unit_price} onChange={(e)=>setLine(i,{unit_price:e.target.value})} data-testid={`order-line-${i}-price`}/></td>
                          <td className="pr-2"><input type="number" step="0.01" min="0" max="100" className="input-dark text-xs text-right" value={ln.discount_pct} onChange={(e)=>setLine(i,{discount_pct:e.target.value})} data-testid={`order-line-${i}-discount`}/></td>
                          <td className="pr-2">
                            <input type="number" step="0.01" min="0" max="1" placeholder={String(form.tax_rate)} className="input-dark text-xs text-right"
                              value={ln.tax_rate ?? ""} onChange={(e)=>setLine(i,{tax_rate: e.target.value===""? null : e.target.value})}
                              data-testid={`order-line-${i}-tax`}/>
                          </td>
                          <td className="pr-2 text-right whitespace-nowrap">{fmtMoney(c.total, form.currency)}</td>
                          <td className="text-right">
                            <button type="button" onClick={()=>removeLine(i)} className="text-[color:var(--text-muted,#737373)] hover:text-red-600" data-testid={`order-line-${i}-remove`}><Trash2 size={12}/></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totals */}
            <div className="flex justify-end mt-4">
              <div className="w-full max-w-xs space-y-1 text-sm">
                <TotalRow label={t("order.totals.subtotal")} value={fmtMoney(totals.subtotal, form.currency)} testId="totals-subtotal"/>
                {totals.discount > 0 && <TotalRow label={t("order.totals.discount")} value={"− " + fmtMoney(totals.discount, form.currency)} testId="totals-discount"/>}
                <TotalRow label={t("order.totals.tax")} value={fmtMoney(totals.tax, form.currency)} testId="totals-tax"/>
                <div className="border-t border-[color:var(--border,#E5E5E5)] pt-1 mt-1 font-medium">
                  <TotalRow label={t("order.totals.total")} value={fmtMoney(totals.total, form.currency)} testId="totals-total"/>
                </div>
              </div>
            </div>
          </section>

          {/* Section: notes */}
          <section>
            <div className="label-caps mb-3">{t("order.section.notes")}</div>
            <textarea data-testid="order-form-notes" rows={2} className="input-dark" value={form.notes||""} onChange={(e)=>setField("notes", e.target.value)}/>
          </section>

          {error && <div data-testid="order-form-error" className="text-red-600 text-sm">{error}</div>}

          <div className="flex justify-end gap-3 pt-2 border-t border-[color:var(--border,#E5E5E5)]">
            <button type="button" onClick={()=>setModal(false)} className="btn-ghost">{t("order.modal.cancel")}</button>
            <button type="submit" disabled={saving} data-testid="order-form-save" className="btn-gold">{t("order.modal.save")}</button>
          </div>
        </form>
      </Modal>

      {/* ============ Quick new client modal ============ */}
      <Modal open={clientModalOpen} onClose={()=>setClientModalOpen(false)} title={t("client.modal.new")}>
        <form onSubmit={saveNewClient} className="space-y-4">
          <Field label={t("client.field.client_type")}>
            <select className="input-dark" value={nc.client_type} onChange={(e)=>setNc({...nc, client_type: e.target.value})}>
              {[["shop","Negozio / rivenditore"],["private","Cliente privato"],["showroom","Showroom / agente"],["buyer","Buyer / department store"]].map(([k,l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label={t("client.field.name")} required>
            <input required className="input-dark" value={nc.name} onChange={(e)=>setNc({...nc, name: e.target.value})} data-testid="quick-client-name"/>
          </Field>
          <Field label={t("client.field.email")} required>
            <input required type="email" className="input-dark" value={nc.email} onChange={(e)=>setNc({...nc, email: e.target.value})} data-testid="quick-client-email"/>
          </Field>
          {ncErr && <div className="text-red-600 text-sm" data-testid="quick-client-error">{ncErr}</div>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={()=>setClientModalOpen(false)} className="btn-ghost">{t("client.modal.cancel")}</button>
            <button className="btn-gold" data-testid="quick-client-save">{t("client.modal.save")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="label-caps block mb-2">
        {label}{required && <span className="text-[color:var(--accent,#D4AF37)] ml-1">*</span>}
      </label>
      {children}
      {error && <div className="text-red-600 text-xs mt-1">{error}</div>}
    </div>
  );
}

function TotalRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <span className="text-[color:var(--text-secondary,#525252)]">{label}</span>
      <span>{value}</span>
    </div>
  );
}
